import React from 'react';
import { NavLink } from 'react-router-dom';
import { Shield, ClipboardCheck, X, LayoutDashboard, UserPlus, Clock, Wallet, User, PanelLeftClose, PanelLeftOpen, FileText, Hourglass, GraduationCap, WalletCards, Cake } from 'lucide-react';

const SuperAdminSidebar = ({ isOpen, onClose, collapsed, onToggleCollapse, attendancePendingCount, onboardingPendingCount, offerLetterPendingCount, probationPendingCount, internshipPendingCount, fullAndFinalPendingCount }) => {
  const menuItems = [
    { path: '/superadmin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/superadmin/attendance-requests', icon: ClipboardCheck, label: 'Regularization', badge: attendancePendingCount },
    { path: '/superadmin/onboarding-requests', icon: UserPlus, label: 'Onboarding Requests', badge: onboardingPendingCount },
    { path: '/superadmin/offer-letters', icon: FileText, label: 'Offer Letters', badge: offerLetterPendingCount },
    { path: '/superadmin/attendance', icon: Clock, label: 'Attendance' },
    { path: '/superadmin/payroll', icon: Wallet, label: 'Payroll' },
    { path: '/superadmin/employees', icon: User, label: 'Employees' },
    { path: '/superadmin/birthdays', icon: Cake, label: 'Birthday List' },
    { path: '/superadmin/probation', icon: Hourglass, label: 'Probation Status', badge: probationPendingCount },
    { path: '/superadmin/internship', icon: GraduationCap, label: 'Internship Status', badge: internshipPendingCount },
    { path: '/superadmin/full-and-final', icon: WalletCards, label: 'Full & Final Settlement', badge: fullAndFinalPendingCount },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 border-r border-white/70 bg-white/90 shadow-[12px_0_50px_rgba(15,23,42,0.08)] backdrop-blur-xl transform transition-all duration-300 ease-in-out lg:translate-x-0
        ${collapsed ? 'lg:w-24' : 'lg:w-72'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="group absolute -right-4 top-1/2 z-20 hidden -translate-y-1/2 lg:block">
          <button
            onClick={onToggleCollapse}
            className="inline-flex h-[50px] w-[50px] items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-700"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen className="h-6 w-6" /> : <PanelLeftClose className="h-6 w-6" />}
          </button>
          <span className="pointer-events-none absolute left-full top-1/2 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-md transition group-hover:opacity-100 xl:block">
            {collapsed ? 'Expand' : 'Collapse'}
          </span>
        </div>

        {/* Logo / brand */}
        <div className={`flex items-center justify-between border-b border-slate-200/80 bg-gradient-to-r from-slate-950 via-indigo-950 to-indigo-900 px-5 py-4 ${collapsed ? 'lg:justify-center lg:px-3' : ''}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white shadow-inner shadow-black/10">
              <Shield className="h-5 w-5" />
            </div>
            <div className={collapsed ? 'lg:hidden' : ''}>
              <span className="block text-sm font-semibold tracking-wide text-white">Super Admin</span>
              <span className="block text-xs text-indigo-200">Operations</span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="lg:hidden rounded-full p-1.5 text-indigo-200 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="px-3 py-4">
          <ul className="space-y-1.5">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `group relative flex items-center rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-150 ${
                        collapsed ? 'lg:justify-center lg:px-2.5' : ''
                      } ${
                        isActive
                          ? 'bg-gradient-to-r from-indigo-50 to-white text-indigo-700 ring-1 ring-inset ring-indigo-100 shadow-sm'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`
                    }
                    onClick={onClose}
                  >
                    <span className={`mr-3 flex h-9 w-9 items-center justify-center rounded-xl bg-white/80 ring-1 ring-slate-200 transition group-hover:bg-white ${collapsed ? 'lg:mr-0' : ''}`}>
                      <Icon className="h-4 w-4 flex-shrink-0" />
                    </span>
                    <span className={`flex-1 tracking-[-0.01em] ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                    {collapsed && (
                      <span className="pointer-events-none absolute left-full top-1/2 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-md transition group-hover:opacity-100 lg:block">
                        {item.label}
                      </span>
                    )}
                    {item.badge != null && item.badge > 0 && (
                      <span className={`ml-2 inline-flex min-w-[22px] items-center justify-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 ${collapsed ? 'lg:hidden' : ''}`}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                    {item.badge != null && item.badge > 0 && collapsed && (
                      <span className="absolute right-2 top-2 inline-flex h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-white" title={`${item.badge > 99 ? '99+' : item.badge} pending`} />
                    )}
                    {item.badge != null && item.badge > 0 && collapsed && <span className="sr-only">{item.badge > 99 ? '99+' : item.badge}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default SuperAdminSidebar;
